/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { PERMISSIONS } from '@squiz/dxp-auth';
import { useAncestorPath } from '@squiz/dxp-console-support';
import {
  ActionMenu,
  ActionMenuProps,
  Breadcrumbs,
  BreadcrumbsProps,
  BUTTON_FORMAT_SOFT,
  DropdownItemData,
  Tab,
  Tabs,
} from '@squiz/sds';
import { DxpScreen } from 'apps/console-core/src/Components/DxpScreen/DxpScreen';
import { useCapabilityBreadcrumbs } from 'apps/console-core/src/Hooks/UseCapabilityBreadcrumbs/UseCapabilityBreadcrumbs';
import { ERROR_SCREEN_TYPES } from 'apps/console-core/src/Screens/DxpErrorScreen/DxpErrorScreen.constants';
import { LoadingScreenEmpty } from 'apps/console-core/src/Screens/LoadingScreenEmpty/LoadingScreenEmpty';
import { getSlug } from 'apps/console-core/src/Utilities/GetSlug/GetSlug';
import { hasPermission } from 'apps/console-core/src/Utilities/HasPermission/HasPermission';
import { uniqueId } from 'lodash';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { CDP_STRINGS } from '../../Constants/Strings.constants';
import { useCustomerDelete } from '../../Hooks/UseCustomerDelete/UseCustomerDelete.hook';
import { useScvCustomer, useScvSchema } from '../../Services/single-customer-view';
import { CustomerAttributes } from './elements/CustomerAttributes/CustomerAttributes';
import { CustomerDetails } from './elements/CustomerDetails/CustomerDetails';

/**
 * The Single Customer View customer profile screen.
 */
export const CustomerProfile = (): JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const parentPath = useAncestorPath(false, 1);
  const onDelete = useCallback((): void => {
    navigate(parentPath);
  }, [navigate, parentPath]);
  const { openDeleteCustomerModal } = useCustomerDelete({
    onDelete,
  });

  const [activeTabIndex, setActiveTabIndex] = useState<number>(0);

  const customerId = getSlug();
  const [customer, apiError] = useScvCustomer(customerId);
  const schema = useScvSchema();

  // Labels
  const labels = useMemo(() => {
    return {
      attributes: t(CDP_STRINGS.attributes),
      customerProfile: t(CDP_STRINGS.customerProfile),
      customers: t(CDP_STRINGS.customers),
      details: t(CDP_STRINGS.details),
    };
  }, [t]);

  const capabilityBreadcrumbs = useCapabilityBreadcrumbs();

  const customerProfileTabs = useMemo((): Array<Tab> => {
    if (!customer || !schema) {
      return [];
    }

    return [
      {
        content: <CustomerDetails customer={customer} setProfileTabIndex={setActiveTabIndex} />,
        key: `customer-details`,
        label: labels.details,
      },
      {
        content: <CustomerAttributes customer={customer} schema={schema} />,
        key: `customer-attributes`,
        label: labels.attributes,
      },
    ];
  }, [customer, labels, schema, setActiveTabIndex]);

  const menuItems: Array<DropdownItemData> = useMemo((): Array<DropdownItemData> => {
    const items = [];
    const canDeleteCustomer = hasPermission(PERMISSIONS.dxpSCV.CUSTOMER_DELETE);

    if (canDeleteCustomer) {
      items.push({
        action: (): void => {
          if (customer) {
            const { $customerid, firstName, lastName } = customer;
            openDeleteCustomerModal($customerid, `${firstName} ${lastName}`);
          }
        },
        className: `customer-profile__action-menu-delete`,
        dataID: `customer-profile__action-menu-delete`,
        key: `delete`,
        label: `Delete`,
      });
    }

    return items;
  }, [customer, openDeleteCustomerModal]);

  if (apiError) {
    navigate({
      pathname: `/error`,
      search: `?type=${ERROR_SCREEN_TYPES.noPageAccessError}`,
    });
    return <></>;
  }

  if (!customer || !schema) {
    return <LoadingScreenEmpty />;
  }

  const { $customerid, firstName, lastName } = customer;

  // Heading should use full name (first + last) if both are present
  // Otherwise, just use customerId instead
  const heading = firstName && lastName ? `${firstName} ${lastName}` : $customerid;

  const breadcrumbProps: BreadcrumbsProps = {
    currentPageName: heading,
    links: [
      ...capabilityBreadcrumbs,
      {
        name: labels.customers,
        url: parentPath,
      },
    ],
  };
  const breadcrumbs = <Breadcrumbs {...breadcrumbProps} data-id="scv-customer-profile-breadcrumbs" />;

  const screenHeading = heading;
  const screenContent = (
    <>
      <Tabs
        activeTabIndex={activeTabIndex}
        listLabel={labels.customerProfile}
        setActiveTabIndex={setActiveTabIndex}
        tabList={customerProfileTabs}
      />
    </>
  );

  const actionMenuProps: ActionMenuProps = {
    buttonFormat: BUTTON_FORMAT_SOFT,
    buttonLabel: `Customer profile actions`,
    itemList: menuItems,
    sectionKey: `customer-profile-action-menu`,
  };

  return (
    <DxpScreen
      breadcrumbs={breadcrumbs}
      buttons={[<ActionMenu {...actionMenuProps} key={uniqueId()} />]}
      heading={screenHeading}
      showSeparator
    >
      {screenContent}
    </DxpScreen>
  );
};
